import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FC, ReactNode } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Button, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { defineStyles } from '../../theme';

const styles = defineStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75, // 6px
    padding: '4px 12px',
    borderRadius: 8,
    backgroundColor: '#ffffff22',
    '&:hover': {
      backgroundColor: '#ffffff33',
    },
  },
  rootError: {
    backgroundColor: 'rgba(254, 226, 226, 0.9)',
    '&:hover': {
      backgroundColor: 'rgba(254, 202, 202, 0.95)',
    },
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: '#22C55E',
    flexShrink: 0,
  },
  dotError: {
    backgroundColor: '#EF4444',
  },
  label: {
    fontFamily: 'Inter, Roboto, sans-serif',
    fontSize: 11,
    fontWeight: 700,
    color: (theme) => theme.palette.common.white,
  },
  labelError: {
    color: '#DC2626',
  },
  icon: {
    width: 12,
    height: 12,
    color: (theme) => theme.palette.common.white,
  },
  iconError: {
    color: '#DC2626',
  },
});

export type ServiceBadgeKind = 'qb' | 'catiex' | 'lb';

export type ServiceBadgeProps = {
  kind: ServiceBadgeKind;
  isError?: boolean;
  href?: string;
};

const TYPE: Record<ServiceBadgeKind, { label: string; tooltip?: { success: ReactNode; error: ReactNode } }> = {
  qb: { label: 'QB' },
  catiex: {
    label: 'CATIEX',
    tooltip: {
      success: (
        <FormattedMessage id="serviceBadge.catiex.tooltip.success" defaultMessage="Projekt v CATIEX je založen." />
      ),
      error: (
        <FormattedMessage
          id="serviceBadge.catiex.tooltip.error"
          defaultMessage="Projekt v CATIEX není založen. Přejděte do QreBuilderu a založte CATIEX projekt."
        />
      ),
    },
  },
  lb: {
    label: 'LB',
    tooltip: {
      success: (
        <FormattedMessage id="serviceBadge.lb.tooltip.success" defaultMessage="Projekt v Linux Box je založen." />
      ),
      error: (
        <FormattedMessage
          id="serviceBadge.lb.tooltip.error"
          defaultMessage="Projekt v Linux Box není založen. Založí se až po vytvoření projektu v CATIEX."
        />
      ),
    },
  },
};

export const ServiceBadge: FC<ServiceBadgeProps> = ({ kind, isError = false, href }) => {
  const type = TYPE[kind];

  return (
    <Tooltip title={isError ? TYPE[kind].tooltip?.error : TYPE[kind].tooltip?.success}>
      <span>
        <Button
          onClick={() => (isError || !href ? undefined : window.open(href, '_blank'))}
          disabled={isError}
          sx={[styles.root, isError && styles.rootError]}
        >
          <Box sx={[styles.dot, isError && styles.dotError]} />
          <Typography component="span" sx={[styles.label, isError && styles.labelError]}>
            {type.label}
          </Typography>
          {isError ? (
            <ErrorOutlineIcon sx={[styles.icon, styles.iconError]} fontSize="small" />
          ) : href ? (
            <OpenInNewIcon sx={styles.icon} fontSize="small" />
          ) : null}
        </Button>
      </span>
    </Tooltip>
  );
};

export default ServiceBadge;
